import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Checkbox, Space, Dropdown } from "antd";
import {
  getTableData,
  setSelectedTableItems,
  setLastSelectedTableItem,
  setTableLoadingState,
} from "@/pages/shared/shared.reducer";
import generateTableSelectMenuItems from "@/common/tableSelect";
import { useDispatch, useSelector, useStore } from "react-redux";
import get from "lodash.get";
import PureTableComponent from "@/common/pure-table";

const CheckboxAllView = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSelectPage = () => {
    props.setTableLoading(props.id, true);
    let data = props.data;
    data = props.getCheckboxProps
      ? data.filter((selected) => !props.getCheckboxProps(selected).disabled)
      : data;
    props.setSelectedTableItems(props.id, [...data]);
    setTimeout(() => {
      setIsOpen(false);
      props.setTableLoading(props.id, false);
    }, 300);
  };
  const handleSelectAll = async () => {
    const includeCart =
      props.id === "approvalSummaryTable" || props.id === "cartManagementTable";
    const cartDocumentIds = includeCart && props.cartDocumentIds;
    try {
      props.setTableLoading(props.id, true);
      const requestData = {
        ...props.filters,
        page: 1,
        pageSize: props.tableCount || props.total,
        favouritesOnly: props.filters?.favouritesOnly || false,
        cartDocumentIds: cartDocumentIds || [],
      };
      let request = {};
      if (props.fetchAll) {
        request = await props.fetchAll();
      } else if (props.fetch) {
        request = await props.fetch(requestData);
      } else {
        console.error("Neither fetchAll nor fetch props are available");
        throw new Error("Data fetching methods not available");
      }

      let allData = request.data?.data || [];
      allData = props.getCheckboxProps
        ? allData.filter(
            (selected) => !props.getCheckboxProps(selected).disabled
          )
        : allData;
      props.setSelectedTableItems(props.id, [...allData]);
    } catch (error) {
      console.error("Error selecting all items:", error);
    } finally {
      setIsOpen(false);
      props.setTableLoading(props.id, false);
    }
  };

  const handleSelectNone = () => {
    props.setTableLoading(props.id, true);
    props.setSelectedTableItems(props.id, []);
    setTimeout(() => {
      setIsOpen(false);
      props.setTableLoading(props.id, false);
    }, 300);
  };

  const handleDropDownVisibleChange = (open) => {
    setIsOpen(open);
  };

  const selectedItems = props.selectedItems || [];
  let tableItems = props.data || [];
  tableItems = props.getCheckboxProps
    ? tableItems.filter(
        (selected) => !props.getCheckboxProps(selected).disabled
      )
    : tableItems;

  var indeterminate =
    selectedItems.length != 0 && selectedItems.length < tableItems.length;
  var checkAll =
    selectedItems.length != 0 && selectedItems.length >= tableItems.length;

  const icon = isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
  const iconColour = isOpen ? "var(--dms-color-link)" : "var(--dms-color-text-muted)";

  const dropdownItems = generateTableSelectMenuItems({
    selectedCount: selectedItems.length,
    currentPageCount: tableItems.length > props.pageSize ? props.pageSize : tableItems.length,
    total: props.tableCount || props.total,
    onSelectPage: () => handleSelectPage(),
    onSelectAll: () => handleSelectAll(),
    onSelectNone: () => handleSelectNone(),
    isSelectAllLimited: (props.tableCount || props.total) > 500,
  });

  return (
    <Dropdown
      open={isOpen}
      trigger={["click"]}
      onOpenChange={handleDropDownVisibleChange}
      classNames={{ root: "table-selection-dropdown" }}
      menu={{ items: dropdownItems, className: "table-selection-menu" }}
    >
      <Space
        onClick={(e) => e.preventDefault()}
        style={{ marginLeft: "5px" }}
        size={1}
      >
        <Checkbox
          aria-label="Select all rows"
          checked={checkAll}
          disabled={tableItems.length == 0}
          indeterminate={indeterminate}
        />
        <i style={{ color: iconColour }} className={icon}></i>
      </Space>
    </Dropdown>
  );
};

const useSelectedItems = (id) =>
  useSelector((state) => get(state.shared, `${id}.selectedItems`));

const useCheckboxActions = () => {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      setSelectedTableItems(id, items, selectedRowKeys) {
        dispatch(setSelectedTableItems(id, items, selectedRowKeys));
      },
      setLastSelectedTableItem(id, item) {
        dispatch(setLastSelectedTableItem(id, item));
      },
      setTableLoading(id, isLoading) {
        dispatch(setTableLoadingState(id, isLoading));
      },
    }),
    [dispatch]
  );
};

export const PureCheckBoxAll = (props) => {
  const { id } = props;
  const cartTable = id === "approvalSummaryTable" ? "approval" : "dashboard";

  const selectedItems = useSelectedItems(id);
  const total = useSelector((state) => get(state.shared, `${id}.count`));
  const filters = useSelector((state) => get(state.shared, `${id}.filters`));
  const pageSize = useSelector(
    (state) => get(state.shared, `${id}.filters.pageSize`) || 150
  );
  const cartDocumentIds = useSelector((state) =>
    get(state.cartManagement, `${cartTable}CartDocumentIds`)
  );
  const { setSelectedTableItems: selectItems, setTableLoading } =
    useCheckboxActions();

  return (
    <CheckboxAllView
      {...props}
      selectedItems={selectedItems}
      total={total}
      filters={filters}
      pageSize={pageSize}
      cartDocumentIds={cartDocumentIds}
      setSelectedTableItems={selectItems}
      setTableLoading={setTableLoading}
    />
  );
};

const CheckboxView = ({
  isChecked,
  readSelection,
  item,
  rowKey,
  setSelectedTableItems,
  setLastSelectedTableItem,
  id,
  index,
  data,
  getCheckboxProps,
  disabled,
}) => {
  const onSelectChange = useCallback(
    (e) => {
      e.stopPropagation();

      const { selectedItems, lastSelectedTableItem } = readSelection();
      let items = selectedItems ? [...selectedItems] : [];
      const checked = e.target.checked;

      if (e.nativeEvent.shiftKey && lastSelectedTableItem !== index) {
        const start = Math.min(index, lastSelectedTableItem);
        const finish = Math.max(index, lastSelectedTableItem);

        for (let i = start; i <= finish; i++) {
          const itemIndex = items.findIndex(
            (x) => rowKey(x) === rowKey(data[i])
          );
          const checkboxProps = getCheckboxProps
            ? getCheckboxProps(data[i])
            : {};

          if (checked && itemIndex === -1 && !checkboxProps.disabled) {
            items.push(data[i]);
          } else if (!checked && itemIndex !== -1) {
            items.splice(itemIndex, 1);
          }
        }
      } else {
        if (checked) {
          items.push(item);
        } else {
          items = items.filter((x) => rowKey(x) !== rowKey(item));
        }
      }

      setSelectedTableItems(id, items);
      setLastSelectedTableItem(id, index);
    },
    [
      readSelection,
      item,
      index,
      data,
      rowKey,
      setSelectedTableItems,
      setLastSelectedTableItem,
      getCheckboxProps,
      id,
    ]
  );

  return (
    <Checkbox
      aria-label="Select row"
      checked={isChecked}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={onSelectChange}
    />
  );
};

// Row checkboxes are rendered once per row (150 by default), so they must not
// subscribe to the whole selectedItems array: that re-renders every row on any
// selection change. Subscribe to a boolean and read the array lazily on click.
export const PureCheckBox = (props) => {
  const { id, item, rowKey } = props;
  const store = useStore();

  const isChecked = useSelector((state) =>
    Boolean(
      get(state.shared, `${id}.selectedItems`)?.some(
        (selectedItem) => rowKey(selectedItem) === rowKey(item)
      )
    )
  );

  const readSelection = useCallback(
    () => ({
      selectedItems: get(store.getState().shared, `${id}.selectedItems`),
      lastSelectedTableItem:
        get(store.getState().shared, `${id}.lastSelectedTableItem`) ?? null,
    }),
    [store, id]
  );

  const {
    setSelectedTableItems: selectItems,
    setLastSelectedTableItem: selectLastItem,
  } = useCheckboxActions();

  return (
    <CheckboxView
      {...props}
      isChecked={isChecked}
      readSelection={readSelection}
      setSelectedTableItems={selectItems}
      setLastSelectedTableItem={selectLastItem}
    />
  );
};

const CommonTableView = (props) => {
  const {
    active,
    currentPage,
    defaultPageSize,
    fetch,
    filters,
    id,
    pageSize,
    refetch,
  } = props;
  const dispatch = useDispatch();

  const hasVisiblePanels = useSelector((state) =>
    Object.keys(state.shared).some(
      (key) => key.endsWith("Panel") && state.shared[key].visible == true
    )
  );

  const loadTableData = useCallback(
    (tableId, fetchFn) => {
      dispatch(getTableData(tableId, fetchFn));
    },
    [dispatch]
  );

  const setTableLoading = useCallback(
    (tableId, isLoading) => {
      dispatch(setTableLoadingState(tableId, isLoading));
    },
    [dispatch]
  );
  const fetchRef = useRef(fetch);
  const filtersRef = useRef(filters);

  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const effectivePageSize =
    pageSize ?? get(filters, "pageSize") ?? defaultPageSize ?? 150;
  const effectiveCurrentPage =
    currentPage ?? get(filters, "currentPage") ?? 1;
  const filterSignature = useMemo(() => JSON.stringify(filters || {}), [filters]);

  useEffect(() => {
    const escFunction = (event) => {
      if (event.keyCode === 27 && !hasVisiblePanels) {
        //onSelectChange([], []);
      }
    };

    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [hasVisiblePanels]);

  useEffect(() => {
    if (refetch) {
      return;
    }

    setTableLoading(id, true);
    if (!fetchRef.current) {
      throw "Could not find function fetch in this.props";
    }

    loadTableData(id, () => {
      const requestData = {
        ...filtersRef.current,
        pageSize: effectivePageSize,
        currentPage: effectiveCurrentPage,
        active: active !== undefined ? active : "true",
      };
      return fetchRef.current(requestData);
    });
  }, [
    active,
    effectiveCurrentPage,
    effectivePageSize,
    filterSignature,
    id,
    loadTableData,
    refetch,
    setTableLoading,
  ]);

  return (
    <PureTableComponent
      identifier={props.identifier}
      tableData={props.tableData || props.data}
      tableCount={props.tableCount}
      id={props.id}
      getCheckboxProps={props.getCheckboxProps}
      checkboxes={props.checkboxes}
      columns={props.columns}
      expandable={props.expandable}
      rowKey={props.rowKey}
      rowClassName={props.rowClassName}
      pageSize={effectivePageSize}
      currentPage={effectiveCurrentPage}
      isResizable={props.isResizable}
      isReorderable={props.isReorderable}
      tableView={props.tableView}
      resetSearch={props.resetSearch}
      getTableHeight={props.getTableHeight}
      customEmpty={props.customEmpty}
      fetch={props.fetch || props.refetch}
      fetchAll={props.fetchAll}
      tableLoading={props.isLoading}
      virtual={props.virtual}
      bodyWrapperClassName={props.bodyWrapperClassName}
      width={props.width}
      formatColumnHoverMode={props.formatColumnHoverMode}
      nativeHorizontalScroll={props.nativeHorizontalScroll}
    />
  );
};

const CommonTable = (props) => {
  const { id } = props;
  const data = useSelector((state) => get(state.shared, `${id}.data`));
  const filters = useSelector((state) => get(state.shared, `${id}.filters`));
  const active = useSelector((state) =>
    get(state.shared, `${id}.filters.active`)
  );
  const defaultPageSize = useSelector(
    (state) => get(state.userDetails, "setting.defaultPageSize") || 150
  );

  return (
    <CommonTableView
      {...props}
      data={data}
      filters={filters}
      active={active}
      defaultPageSize={defaultPageSize}
    />
  );
};

export default CommonTable;